import {AxiosError} from 'axios';
import BASE_URL from '../../config/env';
import {
  BackendErrorData,
  parseApiErrorWithLocalization,
  parseApiErrorWithoutCode,
} from '../../utils/custom-errors/parseApiError';
import {apiClient} from '../apiClient';
import {
  IRedactionHistoryRequest,
  IAnalyzeSearchRequest,
  IPreRedactionReportPayload,
  IGetRedactionViewDataResponse,
  IGetRedactionTagsList,
  IBatchRedactionRollbackResponse,
  IBatchRedactionRollbackPayload,
  IActionMessageResponse,
  IBatchRedactionReportRequest,
  IAddEditRedactionTagRequest,
  IAddEditRedactionTagResponse,
  IBatchRedactionRequest,
} from '../types/redactions.type';

const redactionsEndpoint = (path: string) =>
  `${BASE_URL}/redactions/api/v1/${path}`;

export const getSearchTree = async () => {
  const {data} = await apiClient.get(
    redactionsEndpoint('batchredaction/searchtree'),
    {
      timeout: 60000,
    }
  );
  return data;
};

export const getRedationHistory = async (payload: IRedactionHistoryRequest) => {
  const {data} = await apiClient.post(
    redactionsEndpoint('batchredaction/history'),
    payload
  );

  return data;
};

export const analyzeSearch = async (
  payload: IAnalyzeSearchRequest | string[]
) => {
  try {
    const {data} = await apiClient.post(
      redactionsEndpoint('batchredaction/search'),
      payload
    );
    return data;
  } catch (error: any) {
    throw parseApiErrorWithoutCode(error);
  }
};

export const getAnalysisResult = async (payload: string[]) => {
  const {data} = await apiClient.post(
    redactionsEndpoint('batchredaction/results'),
    payload
  );
  return data;
};

export const preRedactionReportDownload = async (
  payload: IPreRedactionReportPayload
) => {
  try {
    const {data} = await apiClient.post(
      redactionsEndpoint('batchredaction/report'),
      payload
    );
    return data;
  } catch (error: any) {
    throw parseApiErrorWithoutCode(error);
  }
};

export const batchredaction = async (payload: IBatchRedactionRequest) => {
  try {
    const {data} = await apiClient.post(
      redactionsEndpoint('batchredaction/bgqueue'),
      payload
    );
    return data;
  } catch (error: any) {
    throw parseApiErrorWithoutCode(error);
  }
};

export const getRedactionViewData =
  async (): Promise<IGetRedactionViewDataResponse> => {
    const {data} = await apiClient.get(
      redactionsEndpoint(`redaction/view`)
    );
    return data;
  };

export const getRedactionTagsList = async (
  params: string
): Promise<Array<IGetRedactionTagsList>> => {
  const {data} = await apiClient.get(
    redactionsEndpoint(`redaction/tags${params}`)
  );
  return data;
};

export const getRedactionRollback = async (
  payload: IBatchRedactionRollbackPayload
): Promise<IBatchRedactionRollbackResponse> => {
  const endpoint = payload.cancelRollback
    ? 'batchredaction/cancel'
    : 'batchredaction/rollback';

  const {data} = await apiClient.get(redactionsEndpoint(endpoint), {
    params: {batchRedactionId: payload.batchRedactionId},
  });
  return data;
};

export const postBatchRedactionReport = async (
  payload: IBatchRedactionReportRequest
): Promise<IActionMessageResponse> => {
  const {data} = await apiClient.post(
    redactionsEndpoint('batchredaction/report'),
    payload
  );
  return data;
};

export const addRedactionTag = async (
  payload: IAddEditRedactionTagRequest
): Promise<IAddEditRedactionTagResponse> => {
  try {
    const {data} = await apiClient.post(
      redactionsEndpoint('redaction/tags'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'TagsAndFolders'
    );
  }
};

export const editRedactionTag = async ({
  payload,
  tagId,
}: {
  payload: IAddEditRedactionTagRequest;
  tagId: number;
}): Promise<IAddEditRedactionTagResponse> => {
  try {
    const {data} = await apiClient.put(
      redactionsEndpoint(`redaction/tags/${tagId}`),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'TagsAndFolders'
    );
  }
};

export const deleteRedactionTag = async (
  tagId: number
): Promise<IAddEditRedactionTagResponse> => {
  const {data} = await apiClient.delete(
    redactionsEndpoint(`redaction/tags/${tagId}`)
  );
  return data;
};
